import React, { useState, useEffect } from 'react';
import { X, Bell, BellOff, Clock, Check, AlertCircle, Smartphone, Globe } from 'lucide-react';
import { usePushNotifications, COMMON_PRAYER_TIMES, type NotificationTime } from '../../hooks/usePushNotifications';
import { Capacitor } from '@capacitor/core';
import { logger } from '../../utils/logger';

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [notificationState, notificationActions] = usePushNotifications();
  const [selectedTimes, setSelectedTimes] = useState<NotificationTime[]>([]);
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customHour, setCustomHour] = useState(9);
  const [customMinute, setCustomMinute] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const platform = Capacitor.isNativePlatform() ? 'mobile' : 'web';

  // Sync selected times with current notification settings
  useEffect(() => {
    if (notificationState.reminderTimes.length > 0) {
      setSelectedTimes(notificationState.reminderTimes);
    } else if (selectedTimes.length === 0) {
      // Default to morning prayer time
      setSelectedTimes([COMMON_PRAYER_TIMES[0]]);
    }
  }, [notificationState.reminderTimes, selectedTimes.length]);

  if (!isOpen) return null;

  const handleEnableNotifications = async () => {
    try {
      await notificationActions.enableNotifications(selectedTimes);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      logger.debug('Notifications enabled successfully');
    } catch (error) {
      logger.error('Failed to enable notifications', error);
    }
  };

  const handleDisableNotifications = async () => {
    try {
      await notificationActions.disableNotifications();
      logger.debug('Notifications disabled successfully');
    } catch (error) {
      logger.error('Failed to disable notifications', error);
    }
  };

  const handleUpdateTimes = async () => {
    try {
      await notificationActions.updateReminderTimes(selectedTimes);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      logger.debug('Notification times updated successfully');
    } catch (error) {
      logger.error('Failed to update notification times', error);
    }
  };

  const handleAddCustomTime = () => {
    const customTime: NotificationTime = {
      hour: customHour,
      minute: customMinute,
      label: `${customHour}:${customMinute.toString().padStart(2, '0')} ${customHour >= 12 ? 'PM' : 'AM'}`
    };

    // Check if this time already exists
    const exists = selectedTimes.some(time => time.hour === customHour && time.minute === customMinute);

    if (!exists) {
      setSelectedTimes(prev => [...prev, customTime].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)));
    }

    setIsCustomTime(false);
  };

  const handleRemoveTime = (timeToRemove: NotificationTime) => {
    setSelectedTimes(prev => prev.filter(time =>
      !(time.hour === timeToRemove.hour && time.minute === timeToRemove.minute)
    ));
  };

  const handleTestNotification = async () => {
    try {
      await notificationActions.sendTest();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      logger.error('Failed to send test notification', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors duration-300">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center">
            <div className="bg-white bg-opacity-20 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Bell className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Prayer Reminders</h2>
            <p className="text-blue-100">
              Get gentle reminders for your daily prayers and devotions
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Platform Info */}
          <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-2">
              {platform === 'mobile' ? <Smartphone className="w-4 h-4 text-blue-600 dark:text-blue-400" /> : <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
              <span className="text-sm text-blue-800 dark:text-blue-200">
                {platform === 'mobile' ? 'Native mobile notifications' : 'Web browser notifications'}
              </span>
            </div>
          </div>

          {/* Loading State */}
          {notificationState.isLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading notification settings...</p>
            </div>
          )}

          {/* Not Supported */}
          {!notificationState.isLoading && !notificationState.isSupported && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Notifications Not Supported
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Your device or browser doesn't support push notifications.
              </p>
            </div>
          )}

          {/* Error State */}
          {notificationState.error && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-800 dark:text-red-200">
                  {notificationState.error}
                </span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {showSuccess && (
            <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-800 dark:text-green-200">
                  Notification settings updated successfully!
                </span>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!notificationState.isLoading && notificationState.isSupported && (
            <>
              {/* Permission Status */}
              {notificationState.permission && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                    Permission Status
                  </h3>
                  <div className={`p-3 rounded-lg border ${
                    notificationState.permission.granted
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                      : notificationState.permission.denied
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
                  }`}>
                    <span className={`text-sm ${
                      notificationState.permission.granted
                        ? 'text-green-800 dark:text-green-200'
                        : notificationState.permission.denied
                        ? 'text-red-800 dark:text-red-200'
                        : 'text-yellow-800 dark:text-yellow-200'
                    }`}>
                      {notificationState.permission.granted
                        ? '✓ Notifications are enabled'
                        : notificationState.permission.denied
                        ? '✗ Notifications are blocked'
                        : '! Permission not requested yet'
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Quick Setup with Common Times */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Quick Setup
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {COMMON_PRAYER_TIMES.map((time, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        const exists = selectedTimes.some(t => t.hour === time.hour && t.minute === time.minute);
                        if (exists) {
                          handleRemoveTime(time);
                        } else {
                          setSelectedTimes(prev => [...prev, time].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)));
                        }
                      }}
                      className={`p-3 rounded-lg border text-sm transition-all duration-200 ${
                        selectedTimes.some(t => t.hour === time.hour && t.minute === time.minute)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 mx-auto mb-1" />
                      {time.label?.split(' - ')[0]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected Times */}
              {selectedTimes.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                    Selected Times ({selectedTimes.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedTimes.map((time, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {time.label || `${time.hour}:${time.minute.toString().padStart(2, '0')}`}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveTime(time)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Time */}
              <div className="mb-6">
                <button
                  onClick={() => setIsCustomTime(!isCustomTime)}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-500 transition-colors text-sm"
                >
                  + Add Custom Time
                </button>

                {isCustomTime && (
                  <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hour</label>
                        <select
                          value={customHour}
                          onChange={(e) => setCustomHour(Number(e.target.value))}
                          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {i.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minute</label>
                        <select
                          value={customMinute}
                          onChange={(e) => setCustomMinute(Number(e.target.value))}
                          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          {[0, 15, 30, 45].map(minute => (
                            <option key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={handleAddCustomTime}
                        className="mt-5 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {!notificationState.permission?.granted ? (
                  <button
                    onClick={notificationActions.requestPermission}
                    disabled={notificationState.isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Bell className="w-5 h-5" />
                    Enable Notifications
                  </button>
                ) : notificationState.isEnabled ? (
                  <>
                    <button
                      onClick={handleUpdateTimes}
                      disabled={notificationState.isLoading || selectedTimes.length === 0}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Check className="w-5 h-5" />
                      Update Times
                    </button>
                    <button
                      onClick={handleDisableNotifications}
                      disabled={notificationState.isLoading}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <BellOff className="w-5 h-5" />
                      Disable Notifications
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEnableNotifications}
                    disabled={notificationState.isLoading || selectedTimes.length === 0}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Bell className="w-5 h-5" />
                    Enable Prayer Reminders
                  </button>
                )}

                {/* Test Button */}
                {notificationState.permission?.granted && (
                  <button
                    onClick={handleTestNotification}
                    disabled={notificationState.isLoading}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Send Test Notification
                  </button>
                )}
              </div>

              {/* Info */}
              <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  💡 <strong>Tip:</strong> Notifications will remind you at your selected times each day.
                  You can always modify or disable them later.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};